import React, { useMemo, useCallback } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import {
  ColumnDirective,
  ColumnsDirective,
  GridComponent,
  Inject,
  Page,
  Sort,
  Filter,
  ExcelExport,
  PdfExport,
  FilterType,
} from '@syncfusion/ej2-react-grids';

import { useLookupRefundsQuery, RefundView } from '../../../../../services/RefundService';
import { useRefundNavigation } from './hooks';
import {
  PatientTemplate,
  AccountTemplate,
  RefundIdTemplate,
  AmountTemplate,
  StatusChipTemplate,
  PriorityChipTemplate,
  DateTemplate,
} from './templates';

export interface RefundLookupGridProps {
  company: string;
  center: string;
  searchTerm: string;
  testId?: string;
}

// Grid settings
const pageSettings = { pageSize: 20, pageSizes: [10, 20, 50, 100] };
const filterSettings = { type: 'Excel' as FilterType };
const sortSettings = { allowUnsort: false };

// Grid styling
const gridContainerStyles = {
  '& .e-grid': {
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontFamily: 'inherit',
  },
  '& .e-grid .e-gridheader': {
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
  },
  '& .e-grid .e-headercell': {
    fontWeight: 600,
    color: '#1e293b',
    fontSize: '14px',
  },
  '& .e-grid .e-row:hover': {
    backgroundColor: '#f8fafc',
  },
  '& .e-grid .e-altrow': {
    backgroundColor: '#ffffff',
  },
};

/**
 * RefundLookupGrid - Displays refund search results
 * 
 * Follows the established grid pattern with:
 * - Separated templates for cell rendering
 * - Custom hooks for navigation
 * - Clean component structure
 */
export const RefundLookupGrid: React.FC<RefundLookupGridProps> = ({
  company,
  center,
  searchTerm,
  testId,
}) => {
  // Fetch lookup data
  const {
    data: refundResults = [],
    isLoading,
    error,
  } = useLookupRefundsQuery(
    {
      company,
      center,
      searchTerm,
    },
    {
      skip: !company || !center || !searchTerm || searchTerm.length < 3,
    }
  );

  // Use custom hook for navigation
  const { handleRowSelected } = useRefundNavigation(company, center);

  // Create templates
  const patientTemplate = useCallback(
    (rowData: RefundView) => (
      <PatientTemplate firstName={rowData.firstName} lastName={rowData.lastName} />
    ),
    []
  );

  const accountTemplate = useCallback(
    (rowData: RefundView) => <AccountTemplate accountNumber={rowData.accountNumber} />,
    []
  );

  const refundIdTemplate = useCallback(
    (rowData: RefundView) => <RefundIdTemplate id={rowData.id} />,
    []
  );

  const amountTemplate = useCallback(
    (rowData: RefundView) => <AmountTemplate amount={rowData.refundDetail?.amount} />,
    []
  );

  const statusTemplate = useCallback(
    (rowData: RefundView) => <StatusChipTemplate status={rowData.status} />,
    []
  );

  const priorityTemplate = useCallback(
    (rowData: RefundView) => <PriorityChipTemplate priority={rowData.priority} />,
    []
  );

  const startDateTemplate = useCallback(
    (rowData: RefundView) => <DateTemplate date={rowData.refundStartDate} />,
    []
  );

  // Handle loading state
  if (isLoading) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
          Searching refunds...
        </Typography>
        <Typography variant="body2" sx={{ color: '#94a3b8' }}>
          Please wait while we search for refunds matching "{searchTerm}"
        </Typography>
      </Box>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" sx={{ color: '#ef4444', mb: 1 }}>
          Search Error
        </Typography>
        <Typography variant="body2" sx={{ color: '#94a3b8' }}>
          Unable to search for refunds. Please try again.
        </Typography>
      </Box>
    );
  }

  // Handle empty results
  if (refundResults.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
          No refunds found
        </Typography>
        <Typography variant="body2" sx={{ color: '#94a3b8' }}>
          No refunds match your search for "{searchTerm}"
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Results Summary */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ color: '#64748b' }}>
          Found {refundResults.length} refund{refundResults.length !== 1 ? 's' : ''} matching "
          {searchTerm}"
        </Typography>
      </Box>

      <Box sx={gridContainerStyles}>
        <GridComponent
          dataSource={refundResults}
          allowPaging={true}
          allowSorting={true}
          allowFiltering={true}
          allowExcelExport={true}
          allowPdfExport={true}
          pageSettings={pageSettings}
          filterSettings={filterSettings}
          sortSettings={sortSettings}
          height="500px"
          rowSelected={handleRowSelected}
          data-testid={testId}
        >
          <ColumnsDirective>
            <ColumnDirective
              field="patient"
              headerText="Patient"
              width="150"
              textAlign="Left"
              template={patientTemplate}
            />
            <ColumnDirective
              field="accountNumber"
              headerText="Account"
              width="120"
              textAlign="Left"
              template={accountTemplate}
            />
            <ColumnDirective
              field="id"
              headerText="Refund ID"
              width="100"
              textAlign="Left"
              template={refundIdTemplate}
            />
            <ColumnDirective
              field="amount"
              headerText="Amount"
              width="100"
              textAlign="Right"
              template={amountTemplate}
            />
            <ColumnDirective
              field="status"
              headerText="Status"
              width="120"
              textAlign="Center"
              template={statusTemplate}
            />
            <ColumnDirective
              field="priority"
              headerText="Priority"
              width="100"
              textAlign="Center"
              template={priorityTemplate}
            />
            <ColumnDirective
              field="refundStartDate"
              headerText="Start Date"
              width="120"
              textAlign="Center"
              template={startDateTemplate}
            />
          </ColumnsDirective>
          <Inject services={[Page, Sort, Filter, ExcelExport, PdfExport]} />
        </GridComponent>
      </Box>
    </Box>
  );
};

export default RefundLookupGrid;
